<template>
  <DetailsLayout type="address" currency="etc" :icon="currentNetwork">
    <template #left>
      <MainInfoAddress
        :icon="currentNetwork"
        :title="mainInfoTitle"
        :details="addressInfo"
        :copy-value="addressValue"
        :loading="isAddressInfoLoading"
        :is-contract="isContract"
        :contract-name="contractName"
        :contract-symbol="contractSymbol"
        :contract-logo="contractLogo"
      />
    </template>
    <template #right>
      <BitcoinAddressTransactions
        v-if="currentNetwork === NETWORK_ENUMS.bitcoin"
        :data="innerTransactionsList"
        :loading="isTransactionsLoading"
        :hasMore="hasMoreTransactions"
        :is-loading-more="isLoadingMore"
        @load-more="loadMoreTransactions"
        :isLoading="isLoading"
      />
      <TronAddressTransactions
        v-if="currentNetwork === NETWORK_ENUMS.tron"
        :data="innerTransactionsList"
        :loading="isTransactionsLoading"
        :hasMore="hasMoreTransactions"
        :is-loading-more="isLoadingMore"
        @load-more="loadMoreTransactions"
        :active-tab="tronActiveTab"
        @change-type-of-data="changeTronTypeOfData"
      />
      <TONAddressTransactions
        v-if="currentNetwork === NETWORK_ENUMS.ton"
        :data="innerTransactionsList"
        :loading="isTransactionsLoading"
        :hasMore="hasMoreTransactions"
        :is-loading-more="isLoadingMore"
        @load-more="loadMoreTransactions"
      />
      <EthereumAddressTransactions
        v-if="currentNetwork === NETWORK_ENUMS.ethereum"
        :data="innerTransactionsList"
        :loading="isTransactionsLoading"
        :hasMore="hasMoreTransactions"
        :is-loading-more="isLoadingMore"
        :active-tab="tronActiveTab"
        @load-more="loadMoreTransactions"
        @change-type-of-data="changeTronTypeOfData"
      />
      <template
        v-if="currentNetwork === NETWORK_ENUMS.tron && tokenBalancesData.length"
      >
        <TronTokenBalances :token-balances="tokenBalancesData" />
      </template>
      <template
        v-if="currentNetwork === NETWORK_ENUMS.ton && tokenBalancesData.length"
      >
        <TronTokenBalances :token-balances="tokenBalancesData" />
      </template>
    </template>
  </DetailsLayout>
</template>

<script setup>
import { computed, onMounted, ref, getCurrentInstance } from "vue";

const instance = getCurrentInstance();
const { $authlessApiService } = instance.appContext.config.globalProperties;

// router
import { useRoute } from "vue-router";
// store
import { useStore } from "vuex";
// components
import { DetailsLayout } from "@/widgets";
import BitcoinAddressTransactions from "@/widgets/BitcoinAddressTransactions/ui/index.vue";
import TronAddressTransactions from "@/widgets/TronAddressTransactions/ui/index.vue";
import TONAddressTransactions from "@/features/AddressTransactions/TONAddressTransactions.vue";
import EthereumAddressTransactions from "@/features/AddressTransactions/EthereumAddressTransactions.vue";
import { MainInfoAddress } from "@/features";
import { TronTokenBalances } from "@/widgets";
import { NETWORK_ENUMS } from "@/shared/helpers";

const route = useRoute();
const store = useStore();

/**
 * Текущий адрес из маршрута
 */
const addressValue = ref("");
const addressInfo = ref([]);
const isAddressInfoLoading = ref(true);
const isTransactionsLoading = ref(true);
const transactionsList = ref([]);
const tokenBalancesData = ref([]);

/**
 * Fingerprint следующей страницы, если доступен отображается show more
 * @type {Ref<string|null>}
 */
const nextFingerprint = ref(null);
const hasMoreTransactions = computed(() => Boolean(nextFingerprint.value));
/**
 * Флаг подгрузки доп транзакций, чтобы не перерисовывать весь список
 */
const isLoadingMore = ref(false);

const getUserData = computed(() => store.getters["user/getUserData"]);

const currentNetwork = ref(null);
const isContract = ref(false);
const contractName = ref("");
const contractSymbol = ref("");
const contractLogo = ref("");
const isLoading = ref(false);
const innerTransactionsList = ref([]);

const mainInfoTitle = computed(() => {
  const errorAddressTitle = "AddressPage | Error | UnknownNetwork";

  if (currentNetwork.value === null) {
    return errorAddressTitle;
  }

  const addressEnum = {
    bitcoin: "Bitcoin",
    tron: "TRON",
    usdt: "USDT",
    ton: "TON",
    ethereum: "Ethereum",
  };
  return addressEnum[currentNetwork.value] || errorAddressTitle;
});

// Транзакции или трансфер в сети tron
const tronActiveTab = ref("transactions");

/**
 * Загружает список транзакций по адресу.
 * Использует fingerprint (`nextParam`) для пагинации, если доступен.
 *
 * @param {string|null} [nextParam=null] - Параметр пагинации для подгрузки следующих транзакций
 */
const getListOfTransactions = async (nextParam = null) => {
  try {
    if (nextParam) {
      // Если загружаем дополнительные данные
      isLoadingMore.value = true;
    } else {
      // При первичной загрузке используем основной флаг загрузки
      isTransactionsLoading.value = true;
    }

    // добавился новый параметр для трона - /transfers
    // Делаем запрос на него, когда выбрана сеть tron и tronActiveTab = transfers
    const url = `api/v1/${currentNetwork.value}/address/${addressValue.value}/${tronActiveTab.value === "transfers" ? "transfers" : "transactions"}`;
    const query = nextParam ? `?next=${nextParam}` : "";

    // Вызов API с опциональным параметром next
    const { transactions, next } = await $authlessApiService().get(url + query);
    if (nextParam) {
      if (transactions && transactions.length > 0) {
        // Если это подгрузка дополнительных данных и они есть - добавляем к существующим
        transactionsList.value = [...transactionsList.value, ...transactions];
      }
      // Если пришел пустой массив - ничего не делаем с существующими данными
    } else {
      // Первая загрузка - заменяем данные полностью
      transactionsList.value = transactions || [];
      innerTransactionsList.value = transactions.slice(0, 10) || [];
    }

    // Сохраняем fingerprint для следующей страницы, только если он есть и если пришли новые транзакции
    // или убираем кнопку showMore
    if (next && transactions && transactions.length > 0) {
      nextFingerprint.value = next;
    } else {
      nextFingerprint.value = null;
    }
  } catch (error) {
    console.error("Error fetching transactions:", error);
    if (!nextParam) {
      // Очищаем список только при ошибке первичной загрузки
      transactionsList.value = [];
    }
    // При ошибке подгрузки просто оставляем текущие данные и убираем кнопку showMore
    nextFingerprint.value = null;
  } finally {
    isTransactionsLoading.value = false;
    isLoadingMore.value = false;
  }
};

const loadMoreTransactions = async () => {
  innerTransactionsList.value = transactionsList.value.slice(
    0,
    innerTransactionsList.value.length + 10,
  );

  if (nextFingerprint.value && innerTransactionsList.value.length > 190) {
    isLoading.value = true;
    await getListOfTransactions(nextFingerprint.value);
    isLoading.value = false;
  }
};

/**
 * Загружает информацию об адресе.
 * Включает данные о балансе, а также информацию о контракте, если применимо.
 */
async function getAddressDetails() {
  try {
    isAddressInfoLoading.value = true;
    const data = await $authlessApiService().get(
      `api/v1/${currentNetwork.value}/address/${addressValue.value}`,
    );

    addressInfo.value = parseAddressDetailsInfo(data);
    tokenBalancesData.value = data?.balances || [];
    isAddressInfoLoading.value = false;
    isContract.value = data?.is_contract;

    // Set contract extras if available
    if (data?.extras) {
      contractName.value = data.extras.contract_name || "";
      contractSymbol.value = data.extras.contract_symbol || "";
      contractLogo.value = data.extras.logo_url || "";
    }
  } catch (error) {
    console.error("Error fetching address details:", error);
    addressInfo.value = [];
    tokenBalancesData.value = [];
    isAddressInfoLoading.value = false;
  }
}

function parseAddressDetailsInfo(data) {
  return [
    {
      id: 2,
      label: "Amount",
      value: `${data.balance.amount} ${data.balance.symbol}`,
      usdValue: `$\u202F${data.balance.usd_value}`,
    },
    {
      id: 3,
      label: "Hash",
      value: data.address,
    },
  ];
}

const changeTronTypeOfData = (str) => {
  if (tronActiveTab.value !== str) {
    tronActiveTab.value = str;
    // Сбрасываем данные перед новым запросом
    transactionsList.value = [];
    nextFingerprint.value = null;
    isTransactionsLoading.value = true;
    getListOfTransactions();
  }
};

onMounted(() => {
  currentNetwork.value = route.params.network;
  addressValue.value = route.params.id;
  getListOfTransactions();
  getAddressDetails();
  window.emcd.AnalyticsService.track("page_view", {
    event_type: "page_view",
    product: "explorer",
    screen: "address",
    type: getUserData.value ? "internal_address_view" : "external_address_view",
    user_id: getUserData.value ? getUserData.value.userData.user_id : null,
    utm_source: route.query.utm_source || null,
    utm_medium: route.query.utm_medium || null,
    utm_campaign: route.query.utm_campaign || null,
    referrer: route.query.referrer || null,
  });
});
</script>
